import { Component, inject, Input, PipeTransform } from '@angular/core'
import { FileSizePipe } from 'ngx-filesize'
import { MatPaginatorModule } from '@angular/material/paginator'
import { MatTableModule } from '@angular/material/table'
import { TranslateModule } from '@ngx-translate/core'
import { MatSortModule } from '@angular/material/sort'
import { RouterLink } from '@angular/router'
import { AsyncPipe, DatePipe, JsonPipe } from '@angular/common'
import { AdminBooleanValueComponent } from '../../admin/admin-boolean-value/admin-boolean-value.component'
import { DurationPipe } from '../../duration.pipe'
import { MatMenuModule } from '@angular/material/menu'
import { FormsModule } from '@angular/forms'
import { BritishDatePipe } from '../../british-date.pipe'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { AvatarComponent } from '../../avatar/avatar.component'
import { MatChip } from '@angular/material/chips'
import { UsersService } from '../../users.service'

export type ColumnType =
  | 'TEXT'
  | 'DATE'
  | 'DURATION'
  | 'CALL_STATUS'
  | 'COMPLETED_CALL_STATUS'
  | 'BOOLEAN'
  | 'USER'
  | 'USER_AVATAR_LIST'
  | 'CALL'
  | 'ORGANIZATION'
  | 'CALL_USERS'
  | 'JSON'
  | 'LIST'
  | 'NUMBER'
  | 'NUMBER_WITH_ONE_DECIMAL'
  | 'MEETING_PROVIDER'
  | 'UPLOADED_DOCUMENTS'
  | 'STARTS_IN'
  | 'CALL_TYPE'

@Component({
  selector: 'app-paginated-table-cell',
  standalone: true,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    TranslateModule,
    MatSortModule,
    RouterLink,
    FormsModule,
    AdminBooleanValueComponent,
    DurationPipe,
    JsonPipe,
    MatMenuModule,
    MatIcon,
    MatTooltip,
    AvatarComponent,
    MatChip,
    AsyncPipe,
  ],
  providers: [DatePipe, BritishDatePipe],
  templateUrl: './paginated-table-cell.component.html',
  styleUrl: './paginated-table-cell.component.scss',
})
export class PaginatedTableCellComponent {
  @Input({ required: true })
  data!: any

  @Input({ required: true })
  columnType!: ColumnType

  @Input({ required: true })
  timezone!: 'local' | 'british'

  defaultDatePipe = inject(DatePipe)
  britishDatePipe = inject(BritishDatePipe)
  usersService = inject(UsersService)
  currentUser$ = this.usersService.currentUser$
  private fileSizePipe = new FileSizePipe()

  get datePipe(): PipeTransform {
    return this.timezone === 'local' ? this.defaultDatePipe : this.britishDatePipe
  }

  isValidNumber(data: any) {
    return typeof data === 'number' && !isNaN(data)
  }

  formatFileSize(bytes: number): string {
    return this.fileSizePipe.transform(bytes) as string
  }

  formatStartsIn(date: Date | string | null): { text: string; isUpcoming: boolean } | null {
    if (date === null) {
      return null
    }

    const targetDate = typeof date === 'string' ? new Date(date) : date
    const now = new Date()
    const diffMs = targetDate.getTime() - now.getTime()

    if (diffMs <= 0) {
      return null
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays > 0) {
      return { text: `${diffDays}d`, isUpcoming: true }
    } else if (diffHours > 0) {
      const remainingMinutes = diffMinutes % 60
      if (remainingMinutes > 0) {
        return { text: `${diffHours}h ${remainingMinutes}m`, isUpcoming: true }
      }
      return { text: `${diffHours}h`, isUpcoming: true }
    } else {
      return { text: `${diffMinutes}m`, isUpcoming: true }
    }
  }
}
